import AffiliateProductSettingsView from "issara-sdk/apis/AffiliateProductSettingsView_core";
import AffiliateListView from "issara-sdk/apis/AffiliateListView_core";

// serializer
import AffiliateSerializer from "issara-sdk/types/AffiliateSerializer_core";

import {
  ActionHandler,
  BaseSeqState,
  BLC_STATUS,
  GenerateActionTypes,
  generatePrefixedActions,
  PickMainState,
} from "react-lib/apps/HISV3/common/CommonInterface";

import moment from "moment";

/** ============== Type-Defining Constants =============  */

export const ACTIONS = {
  SEARCH_AFFILIATE_LIST: "SEARCH_AFFILIATE_LIST",
  UPDATE_DATE_ALL_PRODUCT: "UPDATE_DATE_ALL_PRODUCT",
  RESET_SETTING_DATE_PRODUCT: "RESET_SETTING_DATE_PRODUCT",
  SAVE_AFFILIATE_PRODUCT: "SAVE_AFFILIATE_PRODUCT",
} as const;

export const SEQUENCE_NAME = "AffiliateSettings" as const;
export const MAIN_CARD_NAME = "CardAffiliateSettings" as const;
const DEFAULT_LIMIT = 20;

/** =============== Types and Interfaces ===============  */
export type State = Partial<{
  AffiliateSettingsSequence?: AffiliateSettingsType;
}>;

export type AffiliateSettingsType = Partial<{
  sequenceIndex?: "Action" | "START" | null;
  filterAffiliate?: {
    searchAffiliate?: Record<string, any>;
    tierAffiliate?: number | null;
    searchProduct?: string;
  };
  settingDateProduct?: {
    startDate?: string;
    startTime?: string;
    endDate?: string;
    endTime?: string;
  };
  affiliateProduct?: Record<string, any>[];
  updatedAffiliateProducts?: {
    id: string | null;
    sourceIndex: number;
  }[];
}> | null;

export type PickedState = PickMainState<
  "buttonLoadCheck" | "errorMessage" | "loadingStatus" | "masterOptions" | "searchedItemListWithKey"
>;

export type Event = { message: "GetMasterData"; params: Record<string, any> };

export type Data = { division?: number; device?: number; masterData?: any };

export type SeqState = BaseSeqState<SequenceName>;

export type Actions = typeof ACTIONS;

type SequenceName = typeof SEQUENCE_NAME;

type ActionPayloadMap = {
  [ACTIONS.SEARCH_AFFILIATE_LIST]: { data: AffiliateSerializer };
  [ACTIONS.UPDATE_DATE_ALL_PRODUCT]: { onSuccess?: () => void };
  [ACTIONS.RESET_SETTING_DATE_PRODUCT]: { onSuccess?: () => void };
  [ACTIONS.SAVE_AFFILIATE_PRODUCT]: {};
};

type ActionTypes = GenerateActionTypes<
  Actions,
  ActionPayloadMap,
  SequenceName,
  PickedState & State
>;

type ActionType = ActionTypes["ActionType"];

export type RunSequence = ActionTypes["RunSequence"];

export type SetProp = ActionTypes["SetProp"];

export type OnEvent = ActionTypes["OnEvent"];

type Params<A extends ActionType["action"]> = ActionTypes["Params"][A];

export type Handler<P = unknown, R = void> = ActionHandler<PickedState & State, Event, Data, P, R>;

export type OptionType = {
  key: string;
  text: string;
  value: string;
};

export type OptionsState = {
  underlyingDisease: OptionType[];
  supplementData: OptionType[];
};

/** ===================== Constants ====================  */
export const StateInitial: State = {
  AffiliateSettingsSequence: null,
};

export const DataInitial: Data = {};

export const BTN_ACTS = generatePrefixedActions(ACTIONS, SEQUENCE_NAME);

export const Masters = [
  ["EncounterStatus", {}],
  ["affiliateGroupListView", {}],
] as const;

/** ===================== Functions ====================  */
export const Start: Handler<SeqState> = async (controller, params) => {
  const state = controller.getState();

  controller.handleEvent({
    message: "GetMasterData",
    params: { masters: Masters },
  });

  const currentDateTime = moment();

  controller.setState({
    AffiliateSettingsSequence: {
      sequenceIndex: "Action",
      filterAffiliate: {
        searchAffiliate: {},
        tierAffiliate: null,
        searchProduct: "",
      },
      affiliateProduct: [],
      settingDateProduct: {
        startDate: currentDateTime.format("DD/MM/YYYY"),
        startTime: currentDateTime.format("HH:mm"),
        endDate: currentDateTime.format("DD/MM/YYYY"),
        endTime: currentDateTime.format("HH:mm"),
      },
    },
  });
};

/** ====================================================  */
/**                        Action                         */
/** ====================================================  */
export const Action: Handler<ActionType> = async (controller, params) => {
  const actionHandlers: Partial<{ [K in ActionType["action"]]: Handler<Params<K>> }> = {
    [ACTIONS.SEARCH_AFFILIATE_LIST]: HandleSearchAffiliateList,
    [ACTIONS.UPDATE_DATE_ALL_PRODUCT]: HandleUpdateDateAllProduct,
    [ACTIONS.RESET_SETTING_DATE_PRODUCT]: HandleResetSettingDateProduct,
    [ACTIONS.SAVE_AFFILIATE_PRODUCT]: HandleSaveAffiliateProduct,
  };
  const { action } = params;

  const handler = actionHandlers[action] as Handler<Params<typeof action>> | undefined;

  if (handler) {
    handler(controller, params);
  } else {
    console.error(`No handler found for action: ${action}`);
  }
};

/** ====================================================  */
/**                        Handler                        */
/** ====================================================  */
export const HandleSearchAffiliateList: Handler<Params<"SEARCH_AFFILIATE_LIST">> = async (
  controller,
  params,
) => {
  const state = controller.getState();

  if (!params.data?.id) {
    return;
  }

  const [affiliateProductRes, affiliateProductErr, affiliateProductNet] =
    await AffiliateProductSettingsView.get({
      params: {
        affiliate: params.data?.id,
      },
      apiToken: controller.apiToken,
      extra: { division: controller.data.division },
    });

  controller.setState({
    AffiliateSettingsSequence: {
      ...state.AffiliateSettingsSequence,
      filterAffiliate: {
        searchAffiliate: params.data,
        tierAffiliate: params.data?.group?.id,
      },
      affiliateProduct: affiliateProductRes?.items,
    },
  });
};

export const HandleUpdateDateAllProduct: Handler<Params<"UPDATE_DATE_ALL_PRODUCT">> = async (
  controller,
  params,
) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.UPDATE_DATE_ALL_PRODUCT]: BLC_STATUS.LOADING,
    },
  });

  const affiliateSettings = state.AffiliateSettingsSequence;

  const searchProduct =
    affiliateSettings?.filterAffiliate?.searchProduct?.trim().toLowerCase() || "";

  const affiliateProduct = affiliateSettings?.affiliateProduct || [];

  const settingDateProduct = affiliateSettings?.settingDateProduct;

  const startedAt = buildDateTime(settingDateProduct?.startDate, settingDateProduct?.startTime);

  const endedAt = buildDateTime(settingDateProduct?.endDate, settingDateProduct?.endTime);

  if (!startedAt || !endedAt) {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.UPDATE_DATE_ALL_PRODUCT]: BLC_STATUS.ERROR,
      },
    });

    return;
  }

  // Existing updated products
  const currentUpdatedProducts = affiliateSettings?.updatedAffiliateProducts || [];
  const updatedProductMap = new Map<number, any>(
    currentUpdatedProducts.map((item: any) => [item.sourceIndex, item]),
  );

  // Update products
  const newAffiliateProduct = affiliateProduct.map((item: any, sourceIndex: number) => {
    const isMatchSearch =
      !searchProduct ||
      item?.product_code?.toLowerCase().includes(searchProduct) ||
      item?.product_name?.toLowerCase().includes(searchProduct);

    if (item.active && isMatchSearch) {
      // เก็บ product ที่ถูกแก้
      if (!updatedProductMap.has(sourceIndex)) {
        updatedProductMap.set(sourceIndex, {
          id: item.id ?? null,
          sourceIndex,
        });
      }

      return {
        ...item,
        started_at: startedAt,
        ended_at: endedAt,
      };
    }

    return item;
  });

  // Updated products
  const updatedAffiliateProducts = Array.from(updatedProductMap.values());

  controller.setState({
    AffiliateSettingsSequence: {
      ...affiliateSettings,
      affiliateProduct: newAffiliateProduct,
      updatedAffiliateProducts,
    },

    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.UPDATE_DATE_ALL_PRODUCT]: BLC_STATUS.SUCCESS,
    },
  });

  params.onSuccess?.();
};

export const HandleResetSettingDateProduct: Handler<Params<"RESET_SETTING_DATE_PRODUCT">> = async (
  controller,
  params,
) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.RESET_SETTING_DATE_PRODUCT]: BLC_STATUS.LOADING,
    },
  });

  let currentDateTime = moment();

  controller.setState({
    AffiliateSettingsSequence: {
      ...state.AffiliateSettingsSequence,
      settingDateProduct: {
        startDate: currentDateTime.format("DD/MM/YYYY"),
        startTime: currentDateTime.format("HH:mm"),
        endDate: currentDateTime.format("DD/MM/YYYY"),
        endTime: currentDateTime.format("HH:mm"),
      },
    },
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.RESET_SETTING_DATE_PRODUCT]: BLC_STATUS.SUCCESS,
    },
  });

  params.onSuccess?.();
};

export const HandleSaveAffiliateProduct: Handler<Params<"SAVE_AFFILIATE_PRODUCT">> = async (
  controller,
  params,
) => {
  const state = controller.getState();
  const affiliateSettings = state.AffiliateSettingsSequence;

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.SAVE_AFFILIATE_PRODUCT]: BLC_STATUS.LOADING,
    },
  });

  const products = affiliateSettings?.affiliateProduct || [];

  const data = (affiliateSettings?.updatedAffiliateProducts || [])
    .map((updated: any) => {
      const product = products[updated.sourceIndex];

      return product
        ? {
            ...product,
            id: updated.id ?? product.id ?? null,
          }
        : null;
    })
    .filter(Boolean);

  const [affiliateProductRes] = await AffiliateProductSettingsView.post({
    data: {
      affiliate: affiliateSettings?.filterAffiliate?.searchAffiliate?.id,
      affiliate_group: affiliateSettings?.filterAffiliate?.tierAffiliate,
      items: data,
    },
    apiToken: controller.apiToken,
    extra: {
      division: controller.data.division,
    },
  });

  if (!affiliateProductRes) {
    controller.setState({
      buttonLoadCheck: {
        ...controller.getState().buttonLoadCheck,
        [BTN_ACTS.SAVE_AFFILIATE_PRODUCT]: BLC_STATUS.ERROR,
      },
    });
    return;
  }

  const [affiliateListRes] = await AffiliateListView.list({
    params: {
      search: affiliateSettings?.filterAffiliate?.searchAffiliate?.code,
    },
    apiToken: controller.apiToken,
    extra: {
      division: controller.data.division,
    },
  });

  controller.setState({
    buttonLoadCheck: {
      ...controller.getState().buttonLoadCheck,
      [BTN_ACTS.SAVE_AFFILIATE_PRODUCT]: BLC_STATUS.SUCCESS,
    },
    AffiliateSettingsSequence: {
      ...controller.getState().AffiliateSettingsSequence,
      updatedAffiliateProducts: [],
    },
  });

  const affiliate = affiliateListRes?.items?.[0];

  if (affiliate) {
    HandleSearchAffiliateList(controller, {
      sequence: "AffiliateSettings",
      action: "SEARCH_AFFILIATE_LIST",
      data: affiliate,
    });
  }
};

const buildDateTime = (date?: string, time?: string) => {
  if (!date || !time) return null;

  const datetime = moment(`${date} ${time}`, "DD/MM/YYYY HH:mm", true);

  if (!datetime.isValid()) {
    return null;
  }

  return datetime.format("YYYY-MM-DDTHH:mm:ssZ");
};
